import { MutationCache, QueryClient } from '@tanstack/react-query'

import { notify } from '@aeris/ui'

import { ApiError } from './api/client.ts'

/**
 * O cache do aplicativo — e o fim do F5 (F211).
 *
 * *"Dar F5 sozinho nas páginas: criei produto e ele não vem, tenho que dar F5 —
 * no PDV, orçamentos, vendas, produtos, tudo."* O que a própria pessoa grava já
 * aparecia (cada mutação invalida a sua lista). O que NÃO aparecia era o que
 * outra pessoa, em outro computador ou outra aba, gravou: nada avisava, e a
 * janela não recarregava nem ao voltar para ela. Três coisas resolvem, da mais
 * barata para a mais cara:
 *
 * 1. **Ao voltar para a janela, as listas velhas recarregam.** O `staleTime`
 *    de 30 s é o freio: foco e desfoco a cada minuto não viram uma leitura por
 *    vez. (O comentário antigo desligava isso inteiro pelo custo; o custo
 *    real é uma leitura por lista vista, e só quando passou de 30 s.)
 * 2. **As listas do dia a dia recarregam a cada 30 s enquanto estão na tela.**
 *    Aba escondida não pergunta nada (`refetchIntervalInBackground: false`).
 * 3. **Uma gravação avisa as outras abas do mesmo navegador**, por
 *    `BroadcastChannel`: o caixa com o PDV numa aba e Produtos na outra vê o
 *    produto novo na hora, sem esperar os 30 s.
 *
 * O servidor empurrando mudança (SSE por domínio) fica para depois: com isto o
 * pior caso é ver em 30 s o que outro computador gravou.
 */

/**
 * As listas que se atualizam sozinhas. Prefixo de `queryKey`.
 *
 * Só listas e saldos: a ficha de UM documento (`sales-document`) fica de fora,
 * porque ela alimenta formulários em edição e recarregar por baixo de quem
 * está digitando é pior do que um dado de 30 s atrás.
 */
export const LIVE_LISTS: readonly string[] = [
  'products',
  'pos-suggest',
  'inbox-product-search',
  'sales-documents',
  'parties',
  'deliveries',
  'finance',
  'stock',
  'stock-balances',
  'exchanges',
  'customer-credit',
  'cash-session',
]

export const LIVE_LIST_INTERVAL_MS = 30_000

/**
 * O que uma gravação deixa velho em QUALQUER tela: as buscas de produto. Elas
 * têm chave própria (`pos-suggest`, `inbox-product-search`), e o formulário
 * de produto só invalidava `products` — o produto novo não aparecia na busca
 * do PDV até o cache de 30 s vencer.
 */
const SEARCHES: readonly string[] = ['pos-suggest', 'inbox-product-search']

export const QUERY_CHANNEL = 'aeris-query'

type Channel = Pick<
  BroadcastChannel,
  'postMessage' | 'addEventListener' | 'close'
>

const openChannel = (): Channel | null =>
  typeof BroadcastChannel === 'undefined'
    ? null
    : new BroadcastChannel(QUERY_CHANNEL)

export const createQueryClient = (
  channel: Channel | null = openChannel()
): QueryClient => {
  const client: QueryClient = new QueryClient({
    mutationCache: new MutationCache({
      /*
       * A rede de segurança de quem grava sem dizer o que deu errado — F248.
       *
       * Doze gravações não tinham `onError` nem mostravam o erro na tela: a
       * falha sumia, e o botão voltava ao normal como se tivesse dado certo
       * (docs/UI_UX.md: nada falha calado). Quem trata o próprio erro, ou o
       * mostra na tela (`meta.errorShownInline`), não recebe o aviso de novo.
       */
      onError: (error, _variables, _context, mutation) => {
        if (mutation.options.onError !== undefined) return
        if (mutation.meta?.errorShownInline === true) return
        notify.error(
          error instanceof ApiError && error.status < 500
            ? error.message
            : 'Não foi possível concluir. Tente de novo — se continuar, avise o suporte.'
        )
      },
      onSuccess: (_data, _variables, _context, mutation) => {
        /*
         * Uma consulta feita como mutação — a busca do PDV — não gravou nada
         * (F212). Antes cada bipe recarregava as sugestões e avisava as
         * outras abas para recarregar tudo, a cada produto passado.
         */
        if (mutation.meta?.readOnly === true) return
        for (const key of SEARCHES) {
          void client.invalidateQueries({ queryKey: [key] })
        }
        // As outras abas: o que elas mostram pode ter acabado de mudar.
        channel?.postMessage({ type: 'changed' })
      },
    }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        retry: 1,
        staleTime: 30_000,
      },
    },
  })

  for (const key of LIVE_LISTS) {
    client.setQueryDefaults([key], {
      refetchInterval: LIVE_LIST_INTERVAL_MS,
      refetchIntervalInBackground: false,
    })
  }

  /*
   * A outra aba gravou: recarrega só o que ESTA aba está mostrando. O resto
   * fica marcado como velho e recarrega quando alguém abrir.
   */
  channel?.addEventListener('message', (event: MessageEvent) => {
    if ((event.data as { type?: unknown } | null)?.type !== 'changed') return
    for (const key of LIVE_LISTS) {
      void client.invalidateQueries({ queryKey: [key], refetchType: 'active' })
    }
  })

  return client
}
